import { mapWithObjectValues } from '@utils/array';
import { swapKeyWithValue } from '@utils/object';
import type { ApiErrors, ApiErrorsArray, KeyOfErrors } from '@repository/types';

export type ErrorDictionary = Record<string, string>;

export const encodeErrors = <T extends ErrorDictionary>(
    errors: ApiErrorsArray,
    dictionary: T
) => {
    const swaped = swapKeyWithValue(dictionary) as Record<string, string>;
    const slugs = mapWithObjectValues(errors, swaped);
    const joined = slugs.join(',');
    return btoa(joined);
};

export const decodeErrors = <T extends ErrorDictionary>(
    errors: string,
    dictionary: T
) => {
    const decoded = atob(errors);
    const splitted = decoded.split(',');
    return mapWithObjectValues(splitted, dictionary);
};

export const getErrorsAsArray = <T extends KeyOfErrors>(
    errorsObj: ApiErrors<T>
) => {
    return (Object.values(errorsObj) as string[][]).flat(1);
};

export const passwordSlugErrorDict = {
    noToken: 'atp.error.user.reset_password.no_token',
    tokenExpired: 'atp.error.user.reset_password.common.error',
} as const;

export const emailSlugErrorDict = {
    alreadyVerified: 'atp.error.user.verify.already_verified',
    expired: 'atp.error.user.verify.expired',
    emptyParameter: 'atp.error.user.empty_parameter',
    invalidCredentials: 'atp.error.user.invalid_credentials',
    common: 'atp.error.user.common.error',
} as const;

export class TrackingError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(`[TrackingError] - ${message}`, options);
        this.name = 'TrackingError';
    }
}

export class LocalesError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(`[LocalesError] - ${message}`, options);
        this.name = 'LocalesError';
    }
}

export class UrlError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(`[UrlError] - ${message}`, options);
        this.name = 'UrlError';
    }
}

export class FetchError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(`[FetchError] - ${message}`, options);
        this.name = 'FetchError';
    }
}

export class ImportError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(`[ImportError] - ${message}`, options);
        this.name = 'ImportError';
    }
}
